import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Button,
  Space,
  Row,
  Col,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowTrendUp,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import DateRangePicker from "../../../commonComponents/DateRangePicker";
import DateSelector from "../../../commonComponents/DateSelector";
import ChartSelection from "../../../commonComponents/ChartSelection";
import BarLineChart from "../../../commonComponents/BarLineChart";
import KeyMetricsSelector from "../../../commonComponents/KeyMetricSelector";
import CustomersDropDown from "../../Loyalty/CustomersDropdown";
import StoresDropDown from "../StoresDropDown";
import CategorySalesSummary from "./CategorySalesSummary";
import CategorySalesRankingCarousel from "./CategorySalesRankingCarousel";
import {
  MainContainer,
  ChartContainer,
  FilterContainer,
  MetricsContainer,
  StyledText,
  BackButton,
  FullWidthSpace,
  FullWidthVerticalSpace,
  PageTitle,
  InheritText,
  ChartSpaceWrapper,
  MetricBadge,
  MetricIcon,
  MetricLabel,
  MetricValue,
  ChartWrapper,
} from "./styles";
import {
  addCommasInPrices,
  Category,
  DateRange,
  Merchant,
  User,
  RankingBy,
  ChartType,
  MetricOption,
} from "./constants";
import { useTheme } from "../../../context/ThemeContext";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchCategorySales, fetchStores as fetchCategoryStores } from "../../../redux/CategorySales";

const { Title, Text } = Typography;

const CategorySales: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  
  // Redux state
  const { categories, keyMetrics, loading, weeklyChange } = useSelector(
    (state: RootState) => state.categorySales
  );
  
  const [rankingBy, setRankingBy] = useState<RankingBy>("revenue");
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [metricOption, setMetricOption] = useState<MetricOption>("totalRevenue");
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment("2023-01-01"),
    endDate: moment(),
  });
  const [chartType, setChartType] = useState<ChartType>("line");
  const [selectedFilter, setSelectedFilter] = useState<string>("");
  const [storeId, setStoreid] = useState<string>("");

  const userString = localStorage.getItem("user");
  const user: User | null = userString ? JSON.parse(userString) : null;

  // Initialize merchant data for non-admin users
  useEffect(() => {
    if (user?.role === "merchant" && !merchant) {
      setMerchant(user as Merchant);
    }
  }, [user, merchant]);

  // Initialize store data for non-admin users
  useEffect(() => {
    if (user?.role !== "admin" && !storeId) {
      dispatch(fetchCategoryStores()).then((result) => {
        if (fetchCategoryStores.fulfilled.match(result) && result.payload.length > 0) {
          setStoreid(result.payload[0].id);
        }
      });
    }
  }, [user, storeId, dispatch]);

  const handleSelection = (selection: DateRange | { startDate: Date | null; endDate: Date | null }) => {
    // Convert Date objects to Moment objects if needed
    let startDate: moment.Moment;
    let endDate: moment.Moment;
    
    if (moment.isMoment(selection.startDate)) {
      startDate = selection.startDate;
    } else if (selection.startDate) {
      startDate = moment(selection.startDate);
    } else {
      startDate = moment("2023-01-01");
    }
    
    if (moment.isMoment(selection.endDate)) {
      endDate = selection.endDate;
    } else if (selection.endDate) {
      endDate = moment(selection.endDate);
    } else {
      endDate = moment();
    }
    
    setSelectedDateRange({ startDate, endDate });
  };

  // Fetch category sales data using Redux
  useEffect(() => {
    if (storeId || user?.role === "admin") {
      const params = isFilter
        ? {
            filter: selectedFilter,
            store: storeId,
            merchantId: merchant?.id,
          }
        : {
            startDate: moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
            endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
            store: storeId,
            merchantId: merchant?.id,
          };
      
      dispatch(fetchCategorySales(params));
    }
  }, [selectedFilter, selectedDateRange, isFilter, merchant, storeId, dispatch]);

  // Toggle ranking between revenue and units sold
  const onToggleRanking = () => {
    setRankingBy(rankingBy === "revenue" ? "unitsSold" : "revenue");
  };

  // Get sorted categories for ranking
  const sortedCategories = useMemo(() => {
    const sorted = [...categories];
    if (rankingBy === "revenue") {
      sorted.sort(
        (a, b) =>
          parseFloat(String(b.totalRevenue || 0)) -
          parseFloat(String(a.totalRevenue || 0))
      );
    } else {
      sorted.sort(
        (a, b) => (b.totalItemsSold || 0) - (a.totalItemsSold || 0)
      );
    }
    return sorted;
  }, [categories, rankingBy]);

  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  return (
    <StyledThemeProvider theme={styledTheme}>
      <MainContainer>
        <FullWidthSpace direction="vertical" size="large">
          {/* Heading with Back Button */}
          <Row align="middle" gutter={16}>
            <Col>
              <BackButton
                icon={<FontAwesomeIcon icon={faArrowLeft} />}
                onClick={() => navigate(-1)}
                shape="circle"
                size="large"
                aria-label="Go Back to Analytics Dashboard"
              />
            </Col>
            <Col flex={1}>
              <FullWidthVerticalSpace direction="vertical" size={0}>
                <Row justify="space-between" align="middle">
                  <Col>
                    <PageTitle level={2}>
                      Category Sales
                    </PageTitle>
                  </Col>
                  <Col>
                    <Space wrap>
                      {user?.role === "admin" && (
                        <Space>
                          <InheritText>By Merchants</InheritText>
                          <CustomersDropDown
                            onOptionSelect={(merch: Merchant) => {
                              setMerchant(merch);
                            }}
                          />
                        </Space>
                      )}
                      <Space>
                        <InheritText>By Store</InheritText>
                        <StoresDropDown
                          onOptionSelect={(store: { id: string }) => {
                            setStoreid(store.id);
                          }}
                          merchantId={merchant?.id}
                          role={user?.role}
                        />
                      </Space>
                    </Space>
                  </Col>
                </Row>
                <StyledText type="secondary">
                  Analyze sales performance by category
                </StyledText>
              </FullWidthVerticalSpace>
            </Col>
          </Row>

          {/* Sales Summary Header */}
          <CategorySalesSummary
            weeklyChange={weeklyChange}
            merchant={merchant}
            storeId={storeId}
          />

          <ChartContainer>
            <ChartSpaceWrapper direction="vertical" size="middle">
              <FilterContainer>
                <DateRangePicker
                  selectedDateRange={selectedDateRange}
                  onDateRangeChange={(range: DateRange) => {
                    setIsFilter(false);
                    setSelectedDateRange(range);
                  }}
                />
                <DateSelector onDateSelect={handleSelection} />
                <KeyMetricsSelector
                  options={[
                    { label: "Revenue", value: "totalRevenue" },
                    { label: "Items Sold", value: "totalItemsSold" },
                  ]}
                  onSelectionChange={(option: string) =>
                    setMetricOption(option as MetricOption)
                  }
                />
                <ChartSelection
                  onChange={(type: string) => setChartType(type as ChartType)}
                />
              </FilterContainer>

              <MetricsContainer>
                <Space align="center" wrap size="large">
                  <MetricBadge align="center" color="#52c41a">
                    <MetricIcon color="#52c41a">
                      <FontAwesomeIcon icon={faArrowTrendUp} />
                    </MetricIcon>
                    <MetricLabel>Total :</MetricLabel>
                    <MetricValue strong color="#52c41a">
                      ${addCommasInPrices(keyMetrics?.totalRevenue || "0.00")}
                    </MetricValue>
                  </MetricBadge>
                  <MetricBadge align="center" color="#1890ff">
                    <MetricIcon color="#1890ff">
                      <FontAwesomeIcon icon={faArrowTrendUp} />
                    </MetricIcon>
                    <MetricLabel>Best Category by Revenue :</MetricLabel>
                    <MetricValue strong color="#1890ff">
                      ${addCommasInPrices(keyMetrics?.bestCategoryByRevenue || "0.00")}
                    </MetricValue>
                  </MetricBadge>
                  <MetricBadge align="center" color="#fa8c16">
                    <MetricIcon color="#fa8c16">
                      <FontAwesomeIcon icon={faArrowTrendUp} />
                    </MetricIcon>
                    <MetricLabel>Best Category by Units Sold :</MetricLabel>
                    <MetricValue strong color="#fa8c16">
                      {keyMetrics?.bestCategoryByUnitsSold || 0}
                    </MetricValue>
                  </MetricBadge>
                  <MetricBadge align="center" color="#722ed1">
                    <MetricIcon color="#722ed1">
                      <FontAwesomeIcon icon={faArrowTrendUp} />
                    </MetricIcon>
                    <MetricLabel>Average Revenue per Category :</MetricLabel>
                    <MetricValue strong color="#722ed1">
                      ${addCommasInPrices(keyMetrics?.averageRevenue || "0.00")}
                    </MetricValue>
                  </MetricBadge>
                </Space>
              </MetricsContainer>

              <ChartWrapper>
                <BarLineChart
                  data={categories}
                  chartType={chartType}
                  xKey={"category"}
                  yKey={metricOption}
                  valueKey={metricOption}
                  isLoading={loading}
                />
              </ChartWrapper>
            </ChartSpaceWrapper>
          </ChartContainer>

          {/* Category Sales Ranking Carousel */}
          {categories.length > 0 && (
            <CategorySalesRankingCarousel
              data={sortedCategories}
              rankingBy={rankingBy}
              onToggleRanking={onToggleRanking}
            />
          )}
        </FullWidthSpace>
    </MainContainer>
    </StyledThemeProvider>
  );
};

export default CategorySales;
